import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = {
  "title": "The Minimum Viable Agent-Ready Design System",
  "slug": "the-minimum-viable-agent-ready-design-system",
  "publishedAt": "2026-06-15T08:00:00.000Z",
  "readTime": "10 min read",
  "excerpt": "You do not need a giant enterprise design system to work safely with AI. You need a small, enforceable system that agents can read and humans can trust.",
  "authorName": "Petri Lahdelma",
  "authorSlug": "petri-lahdelma",
  "status": "scheduled",
  "series": "Agentic Design Systems",
  "tags": ["Design Systems", "AI Governance", "Product Design", "Frontend"],
  "mainImageUrl": "/blog/agentic-design-systems/hero-04-mvp.png",
  "mainImageAlt": "Six-layer minimum viable agent-ready design system stack"
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    figcaption: "figcaption",
    figure: "figure",
    h2: "h2",
    hr: "hr",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ..._provideComponents(),
    ...props.components
  }, {ArticleFigure} = _components;
  if (!ArticleFigure) _missingMdxReference("ArticleFigure", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Not every team needs a cathedral. Some hear “design system” and picture governance councils, forty component owners, and a documentation site with its own roadmap. That shape exists. It is not where most squads should start if agents are already generating UI in the repo."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Thesis:"
      }), " The minimum viable agent-ready design system is small, strict, and explicit — six layers you can enforce in CI before agents outpace your docs. The alternative to a giant system is not chaos. It is a thinner stack where every layer fails loudly when drift appears."]
    }), "\n", _jsxs(_components.p, {
      children: ["The useful target for many teams is a ", _jsx(_components.strong, {
        children: "minimum viable agent-ready design system"
      }), ": small enough to maintain, strict enough to prevent drift, explicit enough that humans and machines can work without hallway context."]
    }), "\n", _jsx(_components.h2, {
      children: "Why “small team magic” stops working"
    }), "\n", _jsx(_components.p, {
      children: "Proximity carries context — who owns brand judgment, which shortcuts are tolerated, which form pattern is canonical. Add contractors, parallel squads, coding agents, or a second repo and proximity stops scaling. Drift shows up as informal button variants, empty states with different voice, forms that validate on different triggers, spacing that becomes local guesswork, dark mode that breaks at the edges, accessibility that arrives in a late audit. None of that requires an enterprise program. All of it requires a real system, just not necessarily a large one."
    }), "\n", _jsxs(_components.p, {
      children: ["Agents accelerate the failure mode. When only four people touched the repo, informal rules lived in glances across the desk. When four people and two agents touch it, the agent copies the first plausible example. Without contracts, the example might be three quarters deprecated. Without token lint, the shortcut becomes ", _jsx(_components.code, {
        children: "#ddd"
      }), " in six files before anyone notices. Without pattern recipes, every form invents its own error anatomy."]
    }), "\n", _jsx(_components.p, {
      children: "You can stay small and still be operational. You cannot stay informal and expect agents to respect taste that was never written down."
    }), "\n", _jsx(_components.h2, {
      children: "Six layers, in order"
    }), "\n", _jsxs(ArticleFigure, {
      layout: "center",
      children: [_jsx("img", {
        src: "/blog/agentic-design-systems/fig-mvp-stack.png",
        alt: "Six layers of a minimum viable agent-ready design system."
      }), _jsx("figcaption", {
        children: "Tokens, components, contracts, patterns, examples, and verification — thin, but enforceable."
      })]
    }), "\n", _jsx(_components.p, {
      children: "Build from the bottom of the stack. Skip a layer and the one above it becomes opinion."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Semantic tokens"
      }), " — Agents and developers should reach for ", _jsx(_components.code, {
        children: "surface"
      }), ", ", _jsx(_components.code, {
        children: "text"
      }), ", ", _jsx(_components.code, {
        children: "border"
      }), ", ", _jsx(_components.code, {
        children: "accent"
      }), ", ", _jsx(_components.code, {
        children: "danger"
      }), ", ", _jsx(_components.code, {
        children: "focus"
      }), " — not ", _jsx(_components.code, {
        children: "gray-800"
      }), " or ad hoc hex. Token lint is the cheapest mechanical gate. In our repo, Stylelint with strict value rules rejects raw colors in component CSS. Start with color and spacing; add motion and elevation when basics hold."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Production components"
      }), " — Coded, with stable props and stories that reflect product reality. Prefer six components used everywhere over twenty that exist only in Figma. Each component gets a folder, CSS modules, stories, tests, and a contract file. If it is not in code, agents will hallucinate it."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Structural contracts"
      }), " — Required stories, a11y flags, token references, lifecycle status. See ", _jsx(_components.a, {
        href: "/blog/component-contracts-are-where-taste-becomes-infrastructure",
        children: "Part 3"
      }), " for v1 vs v2. Ship structural before semantic; honesty about gaps beats pretend coverage."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Pattern recipes"
      }), " — The gaps between atoms: forms, settings panels, destructive flows, table toolbars, empty and error states. Recipes name which components compose, in what order, with which forbidden pairs. Agents compose; recipes constrain composition."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Examples with edge cases"
      }), " — Loading, denial, long copy, narrow viewports, keyboard-only, forced colors. Hero screenshots teach layout; edge stories teach behavior. Agents copy examples. Give them the ones you want repeated."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Verification"
      }), " — Token lint, typecheck, contract validation, axe. Memory is not a strategy. One command that fails in CI is worth fifty Slack reminders."]
    }), "\n", _jsx(_components.p, {
      children: "That is already a design system, even if you call it a UI foundation. Six components with contracts and checks outperform forty components with markdown only, provided someone keeps them current."
    }), "\n", _jsx(_components.h2, {
      children: "Start where the product already hurts"
    }), "\n", _jsx(_components.p, {
      children: "Variant taxonomy debates, documentation migration, and AI generation all feel productive. They delay enforcement. Start where friction is visible."
    }), "\n", _jsx(_components.p, {
      children: "If forms are inconsistent, write a form recipe before you rename another input variant. If dashboards drift, define density and toolbar patterns. If agents misuse components, contract the top five offenders — usually Button, Input, Dialog, Card, and whatever you use for errors. If accessibility arrives late, gate promotion on axe and keyboard coverage in required stories. If brand dilutes, fix semantic token scopes and show real usage in examples, not lavender placeholders."
    }), "\n", _jsx(_components.p, {
      children: "Grow from repeated product friction, not from an abstract inventory. Every layer you add should answer a failure that happened twice."
    }), "\n", _jsx(_components.h2, {
      children: "Agent-readiness without archaeology"
    }), "\n", _jsx(_components.p, {
      children: "A small system is ready when naming is consistent across Figma, code, and Storybook; when contracts and examples describe intent, not only appearance; when one command detects invalid usage; when review spends time on judgment, not mechanics; and when deprecations ship with migration notes in the same PR as the breaking change."
    }), "\n", _jsx(_components.p, {
      children: "The shape can be as small as this:"
    }), "\n", _jsxs(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: [_jsx(_components.figcaption, {
        "data-rehype-pretty-code-title": "",
        "data-language": "json",
        "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
        children: "minimum-agent-ready-system.json"
      }), _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "json",
        "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "json",
          "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#005CC5",
                "--shiki-dark": "#79B8FF",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"components\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Button\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Input\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Select\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Dialog\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Alert\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Card\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "],"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#005CC5",
                "--shiki-dark": "#79B8FF",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"patterns\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Form\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"EmptyState\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"ErrorState\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"TableToolbar\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "],"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#005CC5",
                "--shiki-dark": "#79B8FF",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"checks\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"typecheck\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"validate:components\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"axe\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["For bulk token setup in Figma, some teams use ", _jsx(_components.a, {
        href: "https://github.com/silships/figma-cli",
        children: "figma-cli"
      }), " (Sil Bormüller) to import from ", _jsx(_components.code, {
        children: "DESIGN.md"
      }), ". That is a tool from ", _jsx(_components.a, {
        href: "/blog/tools-harnesses-and-skills-the-missing-model-for-design-systems",
        children: "Part 2"
      }), "; contracts in the repo still define what “done” means in code."]
    }), "\n", _jsx(_components.p, {
      children: "Readiness is not component count. It is whether an agent session can answer: which file is authoritative, which example to copy, which command proves safety, and what happens when the command fails."
    }), "\n", _jsx(_components.h2, {
      children: "Ownership in a small squad"
    }), "\n", _jsx(_components.p, {
      children: "You need a named owner — often the design-system lead or the person who already answers “which button do we use?” Design owns token semantics, pattern recipes, and Storybook examples. Engineering owns contracts, the validation script, and the CI gate. Promotion criteria (experimental → beta → stable) are shared. If nobody owns contracts, agents will still generate UI; the system will not learn from failures."
    }), "\n", _jsx(_components.p, {
      children: "In a squad of eight, this is often four hours a week, not a full role — until agents touch the repo daily, then it becomes the difference between compounding quality and compounding drift. The owner curates which examples agents see, triages validator false positives, and converts repeat review comments into rules."
    }), "\n", _jsx(_components.h2, {
      children: "A 30–60–90 day path"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Days 1–30:"
      }), " Ship semantic color, spacing, type, and focus tokens. Add structural contracts for Button, Input, Dialog, Alert, and Card. Wire ", _jsx(_components.code, {
        children: "npm run validate:components"
      }), " (or your equivalent) into CI. Document the harness: authoritative paths, required commands, human review triggers for taxonomy changes."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Days 31–60:"
      }), " Add form and error-state pattern recipes. Expand stories to cover loading, error, and keyboard paths for top components. Turn on token lint if not already blocking. Demote or mark legacy any story agents keep copying."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Days 61–90:"
      }), " Introduce lifecycle stages in contracts. Write contribution rules with required evidence for promotion. Ship migration notes whenever props change. Review agent failure logs — anything three times becomes a rule, skill, or check."]
    }), "\n", _jsx(_components.p, {
      children: "Treat the timeline as a hypothesis. The point is enforcement before agents outpace your docs. If agents are not in the repo yet, use the same stack for human contributors — the operating model still pays off."
    }), "\n", _jsx(_components.h2, {
      children: "When to stop (or never start)"
    }), "\n", _jsx(_components.p, {
      children: "Defer this stack if UI is still throwaway pre-PMF, if one person owns every surface for the foreseeable year, if agents are not in the repo yet, or if you cannot maintain contracts after the first sprint — unmaintained contracts become lies agents trust."
    }), "\n", _jsx(_components.p, {
      children: "Structural validation catches prop and story drift; it does not replace design review for hierarchy and copy. When the stack fits, output starts closer to acceptable, onboarding shortens, and contributors plug into boundaries instead of reverse-engineering taste from screenshots."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "/blog/from-designops-to-agentops",
        children: "Part 5"
      }), " closes the series: who owns the stack when agents join the delivery loop, and what changes in the design-system lead role."]
    }), "\n", _jsx(_components.h2, {
      children: "Anti-patterns that look like progress"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Documentation sprint without validation."
      }), " Fifty new MDX pages, zero CI gates. Agents read the shiniest page, not the truest one."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Component explosion."
      }), " Adding variants instead of recipes. If agents misuse Dialog, a second dialog component doubles confusion."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Prompt libraries without skills."
      }), " Clever prompts in Notion are not repeatable capabilities. Skills have exit criteria."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Figma-first, code-never."
      }), " Agents ship in the repo. If tokens live only in Figma, code will invent its own palette."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Hero-story-only Storybook."
      }), " Beautiful defaults teach agents that empty, error, and loading states are optional."]
    }), "\n", _jsx(_components.p, {
      children: "Avoiding these is cheaper than fixing production drift later."
    }), "\n", _jsx(_components.h2, {
      children: "Measuring whether the MVP stack is working"
    }), "\n", _jsx(_components.p, {
      children: "Track four signals over a quarter: median time spent on mechanical review comments (should drop), count of agent/human PRs that fail validation before human review (should rise then plateau), number of components with current contracts (should rise), repeat production UI bugs tied to missing patterns (should fall). You do not need a dashboard on day one — a monthly note in the design-system channel is enough."
    }), "\n", _jsx(_components.h2, {
      children: "Pairing the MVP stack with agent workflows"
    }), "\n", _jsx(_components.p, {
      children: "When an agent opens a ticket, the harness should point it to a pattern recipe before codegen. When it opens a component folder, the contract should list required stories to touch. When it opens a PR, CI should fail before a human repeats the same comment as last sprint. The MVP stack is not smaller because it is simpler — it is smaller because each layer has a single job and a single owner."
    }), "\n", _jsx(_components.p, {
      children: "If you are choosing between an eighth component and a second pattern recipe, pick the recipe until agents stop inventing form layouts. If you are choosing between a doc site refresh and token lint, pick lint. Documentation follows enforcement; it does not substitute for it."
    }), "\n", _jsx(_components.p, {
      children: "The MVP stack is deliberately boring. Boring systems survive agent traffic. Exciting doc launches do not."
    }), "\n", _jsx(_components.p, {
      children: "Six components, four patterns, three commands — that is enough to start if each layer has an owner and a failure mode. Scale the stack when repeat friction proves which layer is missing, not when a roadmap template says quarter three."
    }), "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Series:"
      }), " Part 4 of 5 · ", _jsx(_components.strong, {
        children: "←"
      }), " ", _jsx(_components.a, {
        href: "/blog/component-contracts-are-where-taste-becomes-infrastructure",
        children: "Component Contracts"
      }), " · ", _jsx(_components.strong, {
        children: "→"
      }), " ", _jsx(_components.a, {
        href: "/blog/from-designops-to-agentops",
        children: "DesignOps to AgentOps"
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
